/**
 * Stage 2 of chat identity resolution: user ids to directory profiles, in one
 * batched `filter[ids]` lookup on `GET /api/v3/chat/users`.
 *
 * `profiles` is `undefined` until the lookup lands — in flight, or failed when
 * `isError`. Afterwards a user the directory
 * omits — not visible to this caller — is absent from the map.
 */
import { v3 } from "backoffice-api"
import { useEffect, useMemo } from "react"
import type { PeerProfile } from "../types"
import { reportChatError } from "../reportChatError"
import { buildProfileMap } from "./peerProfiles"

export function useDirectoryProfiles(
  userIds: string[],
  { report = true }: { report?: boolean } = {}
): {
  profiles: Map<string, PeerProfile> | undefined
  isError: boolean
} {
  const { data, error } = v3.getChatUsers.useQuery(
    [
      {
        "filter[ids]": userIds.join(","),
        // The whole batch on one page: the directory is not capped
        // server-side, so the default page size would silently truncate it.
        "page[size]": userIds.length,
      },
    ],
    { enabled: userIds.length > 0 }
  )

  useEffect(() => {
    if (error && report)
      reportChatError("failed to resolve chat profiles", error)
  }, [error, report])

  const profiles = useMemo(
    () => (data === undefined ? undefined : buildProfileMap(data.data ?? [])),
    [data]
  )

  return { profiles, isError: !!error }
}
